import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { useGuest } from '../context/GuestContext';
import { useAuth } from '../context/AuthContext';
import { useGamification } from '../context/GamificationContext';
import { toast } from 'sonner';
import { 
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Repeat2,
  Play, ChevronLeft, ChevronRight, Volume2, VolumeX,
  BarChart3, Rocket, Flag, Trash2, Sparkles, Clock, Eye, Loader2, Send, Radio, Pencil, Check, X, Image as ImageIcon, Video, Pin, MapPin
} from 'lucide-react';
import PostInsightsModal from './PostInsightsModal';
import BoostPostModal from './BoostPostModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { API } from '../config/api';
import UserAvatar from './UserAvatar';
import { getProfileUrl, getDisplayHandle } from '../utils/profileUtils';
import { ReactionPicker, ReactionSummary, REACTIONS } from './ReactionPicker';
import { RequestButton } from './RequestButton';
import { AttachmentPreviewCard } from './ContentAttachmentPicker';
import { Textarea } from './ui/textarea';
import { VoicePostPlayer } from './FeedInput';
import { PromoteButton } from './PromoteModal';
import ShareToSocialButton from './ShareToSocialButton';
import { LinkEmbedCard } from './LinkEmbedCard';
import RichText from './RichText';
import { ShareIconButton } from './ShareSheet';
import MonetizeMenu from './MonetizeMenu';
import { TipBadge } from './TipBadge';
import { AddToPlaylistButton } from './AddToPlaylistButton';
import LockedOverlay from './LockedOverlay';
import { BoostButton } from './InfluenceSystem';
import React from 'react';
const ThreadedCommentsLazy = React.lazy(() => import('./ThreadedComments').then(m => ({ default: m.ThreadedComments })));

// Helper function to construct proper media URLs
const getMediaUrl = (url) => {
  if (!url) return '';
  
  // Already a full URL - check if it needs fixing for old deployments
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if this is pointing to an old deployment that no longer exists
    const oldDomains = [
      'restore-dmv.emergent.host',
      'dmv-soundstage-',
      '.preview.emergentagent.com'
    ];
    
    // If URL points to old domain, try to extract the path and use current origin
    for (const oldDomain of oldDomains) {
      if (url.includes(oldDomain)) {
        try {
          const urlObj = new URL(url);
          const path = urlObj.pathname;
          // Use current origin for same-domain access
          if (typeof window !== 'undefined') {
            const newUrl = `${window.location.origin}${path}`;
            return newUrl;
          }
        } catch (e) {
          // Silently handle parse errors in production
        }
      }
    }
    return url;
  }
  
  // Get base URL - for same-origin (production), API is empty string
  // For preview, API has the full URL
  const baseUrl = API ? API.replace('/api', '') : '';
  
  // Already has /api prefix
  if (url.startsWith('/api/')) {
    return `${baseUrl}${url}`;
  }
  
  // Starts with /uploads - need to add /api prefix
  if (url.startsWith('/uploads/')) {
    return `${baseUrl}/api${url}`;
  }
  
  // Any other relative URL
  const apiBase = API || '/api';
  return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Fallback placeholder for broken images
const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23374151" width="400" height="300"/%3E%3Ctext x="200" y="150" fill="%239CA3AF" font-size="14" text-anchor="middle" font-family="system-ui"%3EImage unavailable%3C/text%3E%3Ctext x="200" y="175" fill="%236B7280" font-size="10" text-anchor="middle" font-family="system-ui"%3ETap to retry%3C/text%3E%3C/svg%3E';

/**
 * SpotlightPostCard - Enhanced post card with animations and improved layout
 * 
 * Features:
 * - Larger media previews
 * - Emoji reactions system (replaces simple likes)
 * - Animated interactions
 * - Clearer timestamp and engagement display
 * - Mobile-optimized layout
 */
export function SpotlightPostCard({ post, currentUser }) {
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();
  const { requireAuth } = useGuest();
  const gamification = useGamification();
  // Reactions state
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [reactions, setReactions] = useState({
    total_count: post.likes || 0,
    reaction_counts: post.reaction_counts || {},
    top_reactions: post.top_reactions || []
  });
  const [saved, setSaved] = useState(post.saved_by_me || false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showInsights, setShowInsights] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reactionAnimation, setReactionAnimation] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const videoRef = useRef(null);
  
  // Edit post state
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content || post.content_text || '');
  const [editingPostLoading, setEditingPostLoading] = useState(false);
  const [postContent, setPostContent] = useState(post.content || post.content_text || '');
  const [postIsEdited, setPostIsEdited] = useState(post.is_edited || false);
  
  // Inline comments state
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const commentInputRef = useRef(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [contentUnlocked, setContentUnlocked] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const isLocked = post.content_lock && post.content_lock.lock_type && post.content_lock.lock_type !== 'public' && !contentUnlocked;
  const [editingCommentLoading, setEditingCommentLoading] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [repostCount, setRepostCount] = useState(post.reposts_count || 0);

  const mediaItems = post.media_urls || [];
  const hasMultipleMedia = mediaItems.length > 1;
  
  // Auto-unfurl: detect URLs in content and resolve embed when link_embed is missing
  const [autoEmbed, setAutoEmbed] = useState(null);
  useEffect(() => {
    if (post.link_embed || autoEmbed) return; // Already have embed data
    const text = post.content || post.content_text || '';
    const urlMatch = text.match(/(https?:\/\/[^\s<]+[^\s<.,;:!?'"\])])/i);
    if (!urlMatch) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = isAuthenticated ? getAuthHeaders() : {};
        const res = await fetch(`${API}/embeds/resolve`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: urlMatch[0] })
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (data?.title) setAutoEmbed(data);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [post.post_id]);

  const displayEmbed = post.link_embed || autoEmbed;
  
  const isOwner = currentUser?.user_id === post.user_id || 
                  currentUser?.user_id === post.artist_id ||
                  currentUser?.artist_id === post.artist_id ||
                  currentUser?.user_id === post.author_id ||
                  currentUser?.user_id === post.created_by_user_id;
  const isAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin' || currentUser?.is_owner;
  const canDelete = isOwner || isAdmin;
  const canViewInsights = isOwner || isAdmin;
  const canBoost = isOwner;

  // Fetch comments when expanded
  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/posts/${post.post_id}/comments?limit=50`, { headers });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || data || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // Handle comment button click - toggle inline comments
  // The ThreadedComments component handles its own expand/collapse
  // This just scrolls to the comment section for convenience
  const handleComment = () => {
    if (!requireAuth('comment')) return;
    const el = document.querySelector(`[data-testid="threaded-comments-section"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      // Click toggle button if not already expanded
      const btn = el.querySelector('[data-testid="toggle-comments-btn"]');
      if (btn) btn.click();
    }
  };

  const handleRepost = async () => {
    if (!requireAuth('repost')) return;
    try {
      const res = await fetch(`${API}/posts/${post.post_id}/repost`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setReposted(data.reposted);
        setRepostCount(prev => data.reposted ? prev + 1 : prev - 1);
        toast.success(data.reposted ? 'Reposted!' : 'Repost removed');
      }
    } catch { toast.error('Failed to repost'); }
  };

  // Submit new comment
  const handleSubmitComment = async (e) => {
    e?.preventDefault();
    
    if (!requireAuth('comment')) return;
    
    const trimmedComment = newComment.trim();
    if (!trimmedComment) {
      toast.error('Please enter a comment');
      return;
    }
    
    if (submittingComment) return; // Prevent double submission
    
    setSubmittingComment(true);
    
    // Optimistic UI - add comment immediately
    const optimisticComment = {
      comment_id: `temp_${Date.now()}`,
      content: trimmedComment,
      user_id: user?.user_id,
      author_name: user?.name || user?.handle || 'You',
      author_handle: user?.handle,
      author_avatar: user?.picture || user?.avatar_url,
      created_at: new Date().toISOString(),
      isOptimistic: true
    };
    
    setComments(prev => [optimisticComment, ...prev]);
    setCommentsCount(prev => prev + 1);
    setNewComment('');
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/posts/${post.post_id}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: trimmedComment })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Replace optimistic comment with real one
        setComments(prev => prev.map(c => 
          c.comment_id === optimisticComment.comment_id 
            ? { ...data.comment || data, isOptimistic: false }
            : c
        ));
        toast.success('Comment posted!');
      } else {
        // Revert optimistic update on failure
        setComments(prev => prev.filter(c => c.comment_id !== optimisticComment.comment_id));
        setCommentsCount(prev => prev - 1);
        setNewComment(trimmedComment); // Restore the comment text
        const error = await res.json();
        toast.error(error.detail || 'Failed to post comment');
      }
    } catch (err) {
      // Revert on error
      setComments(prev => prev.filter(c => c.comment_id !== optimisticComment.comment_id));
      setCommentsCount(prev => prev - 1);
      setNewComment(trimmedComment); // Restore the comment text
      console.error('Comment error:', err);
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle enter key to submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  // Format relative time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle reaction selection
  const handleReaction = async (reaction) => {
    if (!requireAuth('react to posts')) return;

    // Trigger animation for new reactions
    if (reaction && !myReaction) {
      setReactionAnimation(true);
      setTimeout(() => setReactionAnimation(false), 600);
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      if (reaction === null) {
        // Remove reaction
        await fetch(`${API}/posts/${post.post_id}/react`, {
          method: 'DELETE',
          headers
        });
        
        setMyReaction(null);
        setReactions(prev => ({
          ...prev,
          total_count: Math.max(0, prev.total_count - 1)
        }));
      } else {
        // Add or change reaction
        const res = await fetch(`${API}/posts/${post.post_id}/react`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ reaction })
        });
        
        const hadReaction = !!myReaction;
        setMyReaction(reaction);
        
        if (!hadReaction) {
          setReactions(prev => ({
            ...prev,
            total_count: prev.total_count + 1
          }));
          // Process gamification XP for new reactions
          if (res.ok) {
            try {
              const data = await res.json();
              if (data.gamification) gamification?.processXpResult({ ...data.gamification, action: 'like_given' });
            } catch {}
          }
        }
      }
    } catch (err) {
      console.error('Reaction error:', err);
      toast.error('Failed to react to post');
    }
  };

  const handleSave = async () => {
    if (!requireAuth('save posts')) return;

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = saved ? 'unsave' : 'save';
      
      await fetch(`${API}/posts/${post.post_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      setSaved(!saved);
      toast.success(saved ? 'Removed from saved' : 'Saved!');
    } catch (err) {
      toast.error('Failed to save post');
    }
  };

  const handleQuickBoost = async () => {
    if (!requireAuth('boost posts')) return;

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/posts/${post.post_id}/quick-boost`, {
        method: 'POST',
        headers
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message || 'Post boosted!');
      } else {
        toast.error(data.detail || 'Failed to boost post');
      }
    } catch (err) {
      console.error('Quick boost error:', err);
      toast.error('Failed to boost post');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API}/posts/${post.post_id}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        const errorMsg = error.detail || 'Failed to delete post';
        console.error('Delete post error:', errorMsg);
        
        // Show specific error messages
        if (response.status === 403) {
          toast.error('Permission denied: You can only delete your own posts');
        } else if (response.status === 404) {
          toast.error('Post not found or already deleted');
        } else {
          toast.error(errorMsg);
        }
        return;
      }
      
      // Success - mark as deleted and dispatch event
      setIsDeleted(true);
      toast.success('Post deleted successfully');
      
      // Dispatch custom event to remove post from all feeds
      window.dispatchEvent(new CustomEvent('postDeleted', { detail: { postId: post.post_id } }));
      
    } catch (err) {
      console.error('Delete post error:', err);
      toast.error(err.message || 'Network error - please try again');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Pin/unpin post handler
  const handleTogglePin = async () => {
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/artist/posts/${post.post_id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_pinned: !post.is_pinned })
      });
      if (res.ok) {
        post.is_pinned = !post.is_pinned;
        toast.success(post.is_pinned ? 'Post pinned to profile' : 'Post unpinned');
        window.dispatchEvent(new CustomEvent('postPinned', { detail: { postId: post.post_id, pinned: post.is_pinned } }));
      } else {
        const err = await res.json();
        toast.error(err.detail?.message || err.detail || 'Failed to pin post');
      }
    } catch { toast.error('Failed to pin post'); }
  };

  // Edit post handler
  const handleEditPost = async () => {
    if (!editedContent.trim()) {
      toast.error('Post content cannot be empty');
      return;
    }
    
    setEditingPostLoading(true);
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${API}/posts/${post.post_id}/edit`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ content_text: editedContent.trim() })
      });
      
      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to edit post');
        return;
      }
      
      // Update local state
      setPostContent(editedContent.trim());
      setPostIsEdited(true);
      setIsEditingPost(false);
      toast.success('Post updated successfully');
      
      // Dispatch event to update post in other views
      window.dispatchEvent(new CustomEvent('postUpdated', { 
        detail: { postId: post.post_id, content: editedContent.trim() } 
      }));
      
    } catch (err) {
      console.error('Edit post error:', err);
      toast.error('Failed to edit post');
    } finally {
      setEditingPostLoading(false);
    }
  };

  const cancelEditPost = () => {
    setEditedContent(postContent);
    setIsEditingPost(false);
  };

  // Edit comment handlers
  const startEditComment = (comment) => {
    setEditingCommentId(comment.comment_id);
    setEditedCommentText(comment.content || comment.comment_text || comment.text || '');
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleEditComment = async (commentId) => {
    if (!editedCommentText.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setEditingCommentLoading(true);
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API}/posts/${post.post_id}/comments/${commentId}/edit`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ comment_text: editedCommentText.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to edit comment');
        return;
      }

      // Update local state
      setComments(prev => prev.map(c => 
        c.comment_id === commentId 
          ? { ...c, content: editedCommentText.trim(), text: editedCommentText.trim(), is_edited: true }
          : c
      ));
      
      setEditingCommentId(null);
      setEditedCommentText('');
      toast.success('Comment updated');

    } catch (err) {
      console.error('Edit comment error:', err);
      toast.error('Failed to edit comment');
    } finally {
      setEditingCommentLoading(false);
    }
  };

  const nextMedia = () => {
    setCurrentMediaIndex(prev => (prev + 1) % mediaItems.length);
    setVideoError(false);
  };

  const prevMedia = () => {
    setCurrentMediaIndex(prev => (prev - 1 + mediaItems.length) % mediaItems.length);
    setVideoError(false);
  };

  const isVideo = (url) => {
    return url?.match(/\.(mp4|webm|mov)$/i) || url?.includes('video');
  };

  const profileUrl = getProfileUrl(post);

  // Don't render if deleted
  if (isDeleted) {
    return null;
  }

  return (
    <>
    <Card className="bg-card border border-border/60 overflow-hidden group hover:border-primary/15 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 rounded-2xl" data-testid="spotlight-post-card" data-post-id={post.post_id}>
      {/* Context Tags */}
      {(post.momentum || post.is_battle || post.voice_id) && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-0">
          {post.momentum === 'blowing_up' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-red-500 to-orange-500">
              <Rocket className="w-3 h-3" /> Blowing Up
            </span>
          )}
          {post.momentum === 'trending' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/15 border border-orange-500/20">
              <BarChart3 className="w-3 h-3" /> Trending
            </span>
          )}
          {post.is_battle && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-500/20">
              Battle
            </span>
          )}
          {post.voice_id && !post.is_battle && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/10 border border-red-500/20">
              <Radio className="w-3 h-3" /> Voice
            </span>
          )}
        </div>
      )}
      {/* Header - Author Info */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between">
          <Link to={profileUrl} className="flex items-center gap-3 group/author">
            <div className="relative">
              <UserAvatar 
                src={post.author_avatar || post.artist_avatar}
                alt={post.author_name || post.artist_name}
                size="lg"
                userId={post.user_id || post.author_id || post.artist_user_id}
                userName={post.author_name || post.artist_name}
                className="ring-2 ring-border group-hover/author:ring-primary/50 transition-all"
              />
              {post.is_verified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground group-hover/author:text-primary transition-colors">
                  {post.author_name || post.artist_name || 'Artist'}
                </span>
                {post.author_tier === 'label' && (
                  <Badge variant="secondary" className="text-xs py-0 px-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    PRO
                  </Badge>
                )}
                {post.is_pinned && (
                  <Badge variant="outline" className="text-xs py-0 px-1.5 border-primary/50 text-primary gap-1" data-testid="pinned-badge">
                    <Pin className="w-2.5 h-2.5" /> Pinned
                  </Badge>
                )}
                {post.location_match === 'local' && (
                  <Badge variant="outline" className="text-xs py-0 px-1.5 border-emerald-500/50 text-emerald-500 gap-1" data-testid="local-badge">
                    <MapPin className="w-2.5 h-2.5" /> Local
                  </Badge>
                )}
                {post.location_match === 'nearby' && (
                  <Badge variant="outline" className="text-xs py-0 px-1.5 border-blue-500/50 text-blue-400 gap-1" data-testid="nearby-badge">
                    <MapPin className="w-2.5 h-2.5" /> Nearby
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{getDisplayHandle(post)}</span>
                <span className="text-xs">•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(post.created_at)}
                </span>
              </div>
            </div>
          </Link>

          {/* More Options */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="post-more-button">
                <MoreHorizontal className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {canViewInsights && (
                <DropdownMenuItem onClick={() => setShowInsights(true)}>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Insights
                </DropdownMenuItem>
              )}
              {canBoost && !post.is_boosted && (
                <>
                  <DropdownMenuItem onClick={handleQuickBoost} className="text-amber-500" data-testid="quick-boost-btn">
                    <Rocket className="w-4 h-4 mr-2" />
                    ⚡ Quick Boost (1 credit)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowBoost(true)}>
                    <Rocket className="w-4 h-4 mr-2" />
                    Full Boost Options
                  </DropdownMenuItem>
                </>
              )}
              {canBoost && post.is_boosted && (
                <DropdownMenuItem disabled className="text-green-500">
                  <Rocket className="w-4 h-4 mr-2 fill-current" />
                  Currently Boosted
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => {
                const ogShareUrl = `${window.location.origin}/api/og/post/${post.post_id}`;
                try {
                  navigator.clipboard.writeText(ogShareUrl).then(() => toast.success('Link copied!')).catch(() => toast.error('Copy failed'));
                } catch { /* clipboard not available */ }
              }}>
                <Share2 className="w-4 h-4 mr-2" />
                Copy Link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSave}>
                <Bookmark className={`w-4 h-4 mr-2 ${saved ? 'fill-current text-yellow-500' : ''}`} />
                {saved ? 'Saved' : 'Save Post'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-yellow-500">
                <Flag className="w-4 h-4 mr-2" />
                Report
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsEditingPost(true)} data-testid="edit-post-menu-item">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Post
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleTogglePin} data-testid="pin-post-menu-item">
                    <Pin className={`w-4 h-4 mr-2 ${post.is_pinned ? 'text-primary fill-current' : ''}`} />
                    {post.is_pinned ? 'Unpin Post' : 'Pin to Profile'}
                  </DropdownMenuItem>
                </>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowDeleteModal(true)} className="text-red-500" data-testid="delete-post-menu-item">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Post
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content Text */}
      {(postContent || isEditingPost) && (
        <div className="px-4 pb-3">
          {isEditingPost ? (
            <div className="space-y-2">
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="What's on your mind?"
                className="min-h-[80px] resize-none"
                maxLength={2000}
                data-testid="edit-post-textarea"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{editedContent.length}/2000</span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEditPost}
                    disabled={editingPostLoading}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleEditPost}
                    disabled={editingPostLoading || !editedContent.trim()}
                    data-testid="save-edit-post-btn"
                  >
                    {editingPostLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Check className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
              <RichText text={postContent} />
              {postIsEdited && (
                <span className="text-xs text-muted-foreground ml-2">(edited)</span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Content Attachment - Display attached song/video/creator */}
      {post.attachment_type && post.attachment_id && (
        <div className="px-4 pb-3" data-testid="post-attachment">
          <AttachmentPreviewCard
            attachment={{
              attachmentType: post.attachment_type,
              attachmentId: post.attachment_id,
              attachmentOwnerId: post.attachment_owner_id,
              attachmentTitle: post.attachment_metadata?.title || 'Attached Content',
              attachmentSubtitle: post.attachment_metadata?.subtitle || post.attachment_metadata?.owner_name || '',
              attachmentCoverUrl: post.attachment_metadata?.cover_url,
              attachmentDuration: post.attachment_metadata?.duration,
              attachmentPlayUrl: post.attachment_metadata?.play_url,
              attachmentOwnerName: post.attachment_metadata?.owner_name
            }}
            onRemove={null}
            showRequest={true}
          />
        </div>
      )}

      {/* Media - Larger Preview */}
      {mediaItems.length > 0 && (
        <div className="relative bg-black/20">
          {/* Locked Content Overlay */}
          {isLocked && !isOwner && (
            <LockedOverlay post={post} onUnlocked={() => setContentUnlocked(true)} />
          )}
          <div className="aspect-[4/3] sm:aspect-video relative overflow-hidden">
            {isVideo(mediaItems[currentMediaIndex]) ? (
              <>
              {!videoError ? (
              <video
                ref={videoRef}
                src={getMediaUrl(mediaItems[currentMediaIndex])}
                className="w-full h-full object-cover"
                loop
                muted={muted}
                playsInline
                autoPlay
                onClick={() => { const v = videoRef.current; if (v) { v.paused ? v.play().catch(() => {}) : v.pause(); } }}
                onError={() => {
                  if (process.env.NODE_ENV !== 'production') {
                    console.error('[SpotlightPostCard] Video load error:', mediaItems[currentMediaIndex]);
                  }
                  setVideoError(true);
                }}
              />
              ) : (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground/70 bg-card cursor-pointer"
                data-testid="video-error-fallback"
                onClick={() => {
                  setVideoError(false);
                }}
              >
                <Video className="w-10 h-10 text-muted-foreground/50" />
                <span className="text-sm font-medium">Video unavailable</span>
                <span className="text-xs text-muted-foreground/50">This media may have been moved or deleted. Tap to retry.</span>
              </div>
              )}
              </>
            ) : (
              <img 
                src={getMediaUrl(mediaItems[currentMediaIndex])} 
                alt=""
                className="w-full h-full object-cover cursor-pointer"
                loading="lazy"
                onClick={(e) => {
                  // Retry loading on click if failed
                  if (e.target.dataset.failed === 'true') {
                    e.target.dataset.failed = 'false';
                    e.target.src = getMediaUrl(mediaItems[currentMediaIndex]);
                  }
                }}
                onError={(e) => {
                  if (process.env.NODE_ENV !== 'production') {
                    console.error('[SpotlightPostCard] Image load error:', mediaItems[currentMediaIndex], '-> Resolved URL:', getMediaUrl(mediaItems[currentMediaIndex]));
                  }
                  e.target.dataset.failed = 'true';
                  e.target.onerror = null;
                  e.target.src = FALLBACK_IMAGE;
                }}
              />
            )}

            {/* Double tap to react indicator */}
            {reactionAnimation && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-7xl animate-ping">❤️</span>
              </div>
            )}

            {/* Video Controls */}
            {isVideo(mediaItems[currentMediaIndex]) && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute bottom-3 right-3 bg-background/50 hover:bg-background/70 h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <div className="absolute top-3 left-3">
                  <Badge className="bg-background/50 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    Video
                  </Badge>
                </div>
              </>
            )}

            {/* Media Navigation */}
            {hasMultipleMedia && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/70 h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); prevMedia(); }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/70 h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); nextMedia(); }}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                
                {/* Dots indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {mediaItems.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        idx === currentMediaIndex ? 'bg-white w-4' : 'bg-muted/300'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Voice Post Player */}
      {post.voice_post && post.audio_url && (
        <div className="px-4 py-2">
          <VoicePostPlayer
            audioUrl={post.audio_url}
            transcript={post.transcript}
            duration={post.audio_duration}
            isBattle={post.is_battle}
          />
        </div>
      )}

      {/* Link Embed */}
      {displayEmbed && (
        <div className="px-4 pb-2">
          <LinkEmbedCard 
            embed={displayEmbed} 
            postId={post.post_id}
            isOwner={isOwner}
            isAdmin={isAdmin}
          />
        </div>
      )}

      {/* Engagement Bar - Facebook Style */}
      <div className="px-4 pb-2">
        {/* Stats Row - Reactions on left, Comments/Shares on right */}
        <div className="flex items-center justify-between text-sm text-muted-foreground py-2 border-b border-border">
          <div className="flex items-center">
            {reactions.total_count > 0 ? (
              <ReactionSummary 
                reactions={reactions}
                onClick={() => {}} // TODO: open reaction details modal
              />
            ) : (
              <span className="text-muted-foreground/50 text-xs">Be the first to react</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs">
            {commentsCount > 0 && (
              <button 
                onClick={handleComment}
                className="hover:underline"
              >
                {commentsCount} {commentsCount === 1 ? 'comment' : 'comments'}
              </button>
            )}
            {post.shares_count > 0 && (
              <span>{post.shares_count} shares</span>
            )}
            {repostCount > 0 && (
              <span>{repostCount} reposts</span>
            )}
            {post.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {post.views.toLocaleString()}
              </span>
            )}
            <TipBadge tipsCount={post.tips_count} tipsTotal={post.tips_total_usd} />
          </div>
        </div>

        {/* Action Buttons - Two-Row Flexbox Layout */}
        <div className="flex flex-col gap-1 pt-1" data-testid="post-action-buttons">
          {/* Row 1: Primary Actions */}
          <div className="flex items-center justify-between gap-1">
            <ReactionPicker
              selectedReaction={myReaction}
              topReactions={reactions.top_reactions}
              onSelect={handleReaction}
              className="flex-1 min-w-0"
            />
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleComment}
              className={`flex-1 min-w-0 gap-1.5 hover:bg-muted/30 rounded-lg ${commentsExpanded ? 'text-primary bg-primary/10' : ''}`}
              data-testid="comment-button"
            >
              <MessageCircle className={`w-4 h-4 flex-shrink-0 ${commentsExpanded ? 'fill-primary/30' : ''}`} />
              <span className="hidden sm:inline text-xs truncate">Comment</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleRepost}
              className={`flex-1 min-w-0 gap-1.5 hover:bg-muted/30 rounded-lg ${reposted ? 'text-green-500' : ''}`}
              data-testid={`repost-btn-${post.post_id}`}
            >
              <Repeat2 className={`w-4 h-4 flex-shrink-0 ${reposted ? 'text-green-500' : ''}`} />
              <span className="hidden sm:inline text-xs truncate">Repost</span>
            </Button>
            
            <RequestButton
              targetType={post.track_id ? 'song' : post.video_id ? 'video' : 'creator'}
              targetId={post.track_id || post.video_id || post.artist_id || post.user_id}
              targetName={post.track_title || post.video_title || post.author_name || 'Content'}
              creatorName={post.author_name || post.artist_name}
              variant="ghost"
              size="sm"
              className="flex-1 min-w-0 gap-1.5 hover:bg-muted/30 rounded-lg hover:text-orange-500"
            />
          </div>

          {/* Row 2: Share, Promote, Boost & Monetize Actions */}
          <div className="flex items-center gap-2 px-1">
            <ShareIconButton
              url={`/post/${post.post_id}`}
              title={`Post by ${post.author_name || 'DMV Artist'}`}
              description={(post.content || post.content_text)?.substring(0, 100)}
              className="w-4 h-4"
            />
            
            <ShareToSocialButton contentType="post" contentId={post.post_id} contentTitle={(post.content || post.content_text)?.substring(0, 60)} caption={(post.content || post.content_text)?.substring(0, 200)} />

            <BoostButton contentType="post" contentId={post.post_id} getAuthHeaders={getAuthHeaders} />
            
            {isOwner && <PromoteButton contentType="post" contentId={post.post_id} contentTitle={(post.content || post.content_text)?.substring(0, 60)} />}
            
            <MonetizeMenu post={post} isOwner={isOwner} />
            
            {post.track_id && (
              <AddToPlaylistButton trackId={post.track_id} size="sm" variant="ghost" />
            )}
          </div>
        </div>
      </div>

      {/* Threaded Comments Section */}
      <div className="px-3 pb-2 border-t border-border/30 pt-2">
        <React.Suspense fallback={<div className="py-2 text-center text-xs text-muted-foreground">Loading comments...</div>}>
          <ThreadedCommentsLazy
            postId={post.post_id}
            commentsCount={post.comments_count || post.comment_count || 0}
            getAuthHeaders={getAuthHeaders}
            currentUser={user || currentUser}
          />
        </React.Suspense>
      </div>

      {/* Modals */}
      {showInsights && (
        <PostInsightsModal
          postId={post.post_id}
          isOpen={showInsights}
          onClose={() => setShowInsights(false)}
        />
      )}
      
      {showBoost && (
        <BoostPostModal
          postId={post.post_id}
          isOpen={showBoost}
          onClose={() => setShowBoost(false)}
        />
      )}
    </Card>
    
    {/* Delete Confirmation Modal */}
    <DeleteConfirmModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDelete}
      title="Delete this post?"
      description="This post will be permanently removed from all feeds."
      itemType="post"
      loading={deleteLoading}
    />
    </>
  );
}

export default SpotlightPostCard;
